import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError, formatDoctorName } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, Edit, Trash2, Stethoscope, ToggleLeft, ToggleRight, CalendarMinus } from "lucide-react";

const EMPTY_FORM = { name: "", email: "", phone: "", specialization: "", department_id: "", room_number: "", consultation_fee: "", password: "Doctor@123" };

export default function Doctors() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [editDoctor, setEditDoctor] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [leaveDate, setLeaveDate] = useState("");
  const [leaveReason, setLeaveReason] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [docRes, deptRes] = await Promise.all([api.get("/doctors"), api.get("/departments")]);
      setDoctors(docRes.data);
      setDepartments(deptRes.data);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAdd = () => { setForm(EMPTY_FORM); setEditDoctor(null); setShowModal(true); };
  const openEdit = (doc) => { setForm({ ...doc, password: "" }); setEditDoctor(doc); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editDoctor) {
        const { password, ...updateData } = form;
        await api.put(`/doctors/${editDoctor.id}`, updateData);
        toast({ title: "Doctor updated successfully" });
      } else {
        await api.post("/doctors", form);
        toast({ title: "Doctor added successfully" });
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this doctor?")) return;
    try {
      await api.delete(`/doctors/${id}`);
      toast({ title: "Doctor deleted" });
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const toggleAvailability = async (doc) => {
    try {
      await api.put(`/doctors/${doc.id}`, { is_available: !doc.is_available });
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const addLeave = async () => {
    if (!leaveDate) return;
    try {
      await api.post(`/doctors/${selectedDoctor.id}/leave`, { date: leaveDate, reason: leaveReason });
      toast({ title: "Leave added" });
      setShowLeaveModal(false);
      setLeaveDate(""); setLeaveReason("");
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const filtered = doctors.filter(d =>
    d.name?.toLowerCase().includes(search.toLowerCase()) ||
    d.specialization?.toLowerCase().includes(search.toLowerCase())
  );

  const getDeptName = (id) => departments.find(d => d.id === id)?.name || "-";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">{t("doctors")}</h1>
          <p className="text-slate-500 text-sm mt-1">Manage clinic doctors and their schedules</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={openAdd} data-testid="add-doctor-btn">
          <Plus size={16} /> Add Doctor
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search doctors..." className="pl-9 border-slate-200" value={search} onChange={e => setSearch(e.target.value)} data-testid="doctor-search-input" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-700">Name</TableHead>
              <TableHead className="font-semibold text-slate-700">Specialization</TableHead>
              <TableHead className="font-semibold text-slate-700">Department</TableHead>
              <TableHead className="font-semibold text-slate-700">Phone</TableHead>
              <TableHead className="font-semibold text-slate-700">Fee (₹)</TableHead>
              <TableHead className="font-semibold text-slate-700">Status</TableHead>
              <TableHead className="font-semibold text-slate-700">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">{t("loading")}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">
                <Stethoscope size={32} className="mx-auto mb-2 opacity-30" />
                <p>No doctors found</p>
              </TableCell></TableRow>
            ) : filtered.map(doc => (
              <TableRow key={doc.id} className="table-row-hover" data-testid={`doctor-row-${doc.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-emerald-800">{doc.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{doc.name}</p>
                      <p className="text-xs text-slate-500">{doc.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-700">{doc.specialization}</TableCell>
                <TableCell className="text-sm text-slate-600">{getDeptName(doc.department_id)}</TableCell>
                <TableCell className="text-sm text-slate-600">{doc.phone}</TableCell>
                <TableCell className="text-sm font-medium text-slate-800">₹{doc.consultation_fee}</TableCell>
                <TableCell>
                  <button onClick={() => toggleAvailability(doc)} className="flex items-center gap-1.5" data-testid={`toggle-avail-${doc.id}`}>
                    {doc.is_available
                      ? <><ToggleRight size={20} className="text-emerald-600" /><span className="text-xs text-emerald-700 font-medium">Available</span></>
                      : <><ToggleLeft size={20} className="text-slate-400" /><span className="text-xs text-slate-500">Unavailable</span></>
                    }
                  </button>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900" onClick={() => openEdit(doc)} data-testid={`edit-doctor-${doc.id}`}>
                      <Edit size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900" onClick={() => { setSelectedDoctor(doc); setShowLeaveModal(true); }} data-testid={`leave-doctor-${doc.id}`}>
                      <CalendarMinus size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-rose-600" onClick={() => handleDelete(doc.id)} data-testid={`delete-doctor-${doc.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-sans">{editDoctor ? "Edit Doctor" : "Add New Doctor"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-slate-700">Full Name</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 border-slate-200" required data-testid="doctor-name-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 border-slate-200" required data-testid="doctor-email-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 border-slate-200" required data-testid="doctor-phone-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Specialization</Label>
                <Input value={form.specialization} onChange={e => setForm({...form, specialization: e.target.value})} className="mt-1 border-slate-200" required data-testid="doctor-spec-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Department</Label>
                <select value={form.department_id} onChange={e => setForm({...form, department_id: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                  <option value="">Select Department</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Room Number</Label>
                <Input value={form.room_number} onChange={e => setForm({...form, room_number: e.target.value})} className="mt-1 border-slate-200" data-testid="doctor-room-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Consultation Fee (₹)</Label>
                <Input type="number" value={form.consultation_fee} onChange={e => setForm({...form, consultation_fee: e.target.value})} className="mt-1 border-slate-200" data-testid="doctor-fee-input" />
              </div>
              {!editDoctor && (
                <div>
                  <Label className="text-sm text-slate-700">Password</Label>
                  <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="mt-1 border-slate-200" required data-testid="doctor-pass-input" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving} data-testid="save-doctor-btn">
                {saving ? "Saving..." : "Save Doctor"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Leave Modal */}
      <Dialog open={showLeaveModal} onOpenChange={setShowLeaveModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-sans">Add Leave for {formatDoctorName(selectedDoctor?.name)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-slate-700">Leave Date</Label>
              <Input type="date" value={leaveDate} onChange={e => setLeaveDate(e.target.value)} className="mt-1 border-slate-200" data-testid="leave-date-input" />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Reason</Label>
              <Input value={leaveReason} onChange={e => setLeaveReason(e.target.value)} placeholder="Optional reason" className="mt-1 border-slate-200" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowLeaveModal(false)}>Cancel</Button>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={addLeave} data-testid="save-leave-btn">Add Leave</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
